import { useState, useEffect } from "react";
import { Eye, EyeOff, ChevronRight, ChevronLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";

interface Column {
  field: string;
  label: string;
  visible?: boolean;
}

interface ColumnVisibilityModalProps {
  columns: Column[];
  onClose: () => void;
  onSave: (columns: Column[]) => void;
}

export default function ColumnVisibilityModal({
  columns,
  onClose,
  onSave,
}: ColumnVisibilityModalProps) {
  const [localColumns, setLocalColumns] = useState<Column[]>([]);

  useEffect(() => {
    setLocalColumns(JSON.parse(JSON.stringify(columns)));
  }, [columns]);

  const visibleColumns = localColumns.filter((col) => col.visible !== false);
  const hiddenColumns = localColumns.filter((col) => col.visible === false);

  const showColumn = (field: string) => {
    setLocalColumns((prev) =>
      prev.map((col) => (col.field === field ? { ...col, visible: true } : col))
    );
  };

  const hideColumn = (field: string) => {
    setLocalColumns((prev) =>
      prev.map((col) =>
        col.field === field ? { ...col, visible: false } : col
      )
    );
  };

  const handleSave = () => {
    onSave(localColumns);
    onClose();
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Visibilidade das Colunas</DialogTitle>
          <DialogDescription>
            Gerencie quais colunas devem aparecer na tabela
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 gap-6">
            {/* Colunas Ocultas */}
            <div className="border rounded-lg">
              <div className="bg-muted px-4 py-3 border-b">
                <div className="flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-muted-foreground" />
                  <h3 className="font-semibold">
                    Colunas Ocultas
                  </h3>
                  <span className="text-sm text-muted-foreground">
                    ({hiddenColumns.length})
                  </span>
                </div>
              </div>
              <div className="p-3 space-y-2 min-h-[300px]">
                {hiddenColumns.length > 0 ? (
                  hiddenColumns.map((col) => (
                    <div
                      key={col.field}
                      className="flex items-center justify-between p-3 bg-background border rounded-md hover:bg-muted/50 transition-colors"
                    >
                      <span className="text-sm font-medium">
                        {col.label}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => showColumn(col.field)}
                        title="Mostrar coluna"
                        className="h-8 w-8 text-primary hover:bg-primary/10"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-[280px] text-muted-foreground">
                    <EyeOff className="w-12 h-12 mb-2" />
                    <p className="text-sm">Nenhuma coluna oculta</p>
                  </div>
                )}
              </div>
            </div>

            {/* Colunas Visíveis */}
            <div className="border rounded-lg">
              <div className="bg-accent px-4 py-3 border-b border-primary/20">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold">
                    Colunas Visíveis
                  </h3>
                  <span className="text-sm text-muted-foreground">
                    ({visibleColumns.length})
                  </span>
                </div>
              </div>
              <div className="p-3 space-y-2 min-h-[300px]">
                {visibleColumns.length > 0 ? (
                  visibleColumns.map((col) => (
                    <div
                      key={col.field}
                      className="flex items-center justify-between p-3 bg-background border rounded-md hover:bg-muted/50 transition-colors"
                    >
                      <span className="text-sm font-medium">
                        {col.label}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => hideColumn(col.field)}
                        title="Ocultar coluna"
                        className="h-8 w-8 hover:bg-muted"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-[280px] text-muted-foreground">
                    <Eye className="w-12 h-12 mb-2" />
                    <p className="text-sm">Nenhuma coluna visível</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
